import { useEffect, useState } from 'react';
import { Minus, Plus, Trash2, ShoppingCart, Tag, Gift, StickyNote } from 'lucide-react';
import { useCarritoStore } from '@renderer/store/useCarritoStore';
import { NotaProductoModal, useNotasRapidas } from '@renderer/components/pos/NotaProducto';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { formatPrecio } from '@renderer/utils/formatters';
import type { MotorResultado, MotorCarritoItem, ProductoVista } from '@shared/types';

interface Props {
  tipo: 'comer_aqui' | 'delivery';
  /**
   * Sprint 111 — El mostrador se llamaba a sí mismo "Comer aquí".
   *
   * En la base es `comer_aqui` sin mesa (Sprint 104) y esta cabecera leía el tipo crudo, así que
   * en Venta de mostrador el carrito decía **"Pedido en curso · Comer aquí"** con el cliente
   * parado esperando su bolsa para llevar. Un canal que se nombra mal en pantalla es un canal que
   * se aprende mal, y quien lee el reporte del día tampoco sabe qué está viendo.
   */
  paraRecoger?: boolean;
  clienteNombre?: string;
  onConfirmar: () => void;
  onCancelar: () => void;
  enviando: boolean;
  /**
   * Sprint 28 — 'agregar' = los productos se anexan a un pedido de mesa ya
   * abierto. Cambia los textos y oculta el preview de promos (al agregar no
   * se re-evalúan — ver RN-S28-04).
   */
  modo?: 'crear' | 'agregar';
  /**
   * Sprint 78 — Renglones que el servidor no puede vender, por índice.
   *
   * Se marcan aquí y no sólo en un aviso: un mensaje que se cierra deja al operador buscando
   * cuál de los ocho renglones era el del problema.
   */
  problemas?: Array<{ indice: number; motivo: string }>;
  /**
   * Sprint 140 — A qué cuenta se están sumando estos productos, dicho con el canal correcto:
   * *"Se suman a la cuenta de la mesa"*, *"…al pedido de Sra. Lupita"*, *"…al domicilio"*.
   */
  destinoAgregar?: string;
  /**
   * Sprint 140 — El negocio ya eligió cobrar al entregar, así que la comanda sale sin
   * comprobante y el pie del carrito no debe seguir pidiéndolo.
   */
  pagaAlEntregar?: boolean;
}

export function Carrito({
  tipo,
  paraRecoger,
  clienteNombre,
  onConfirmar,
  onCancelar,
  enviando,
  modo = 'crear',
  problemas = [],
  destinoAgregar,
  pagaAlEntregar = false,
}: Props) {
  const esAgregar = modo === 'agregar';
  /**
   * Sprint 65 — En domicilio este botón NO manda nada a la cocina.
   *
   * Reporte del negocio: *"levanta el pedido y dice ENVIAR A COCINA, sin que el cliente haya
   * enviado su direccion ni el ticket de transferencia, y despues vuelve a decir enviar a
   * cocina"*. Es literal: en domicilio abre el paso de direccion, y la comanda sale dos pasos
   * despues, al subir el comprobante. El texto era el de una mesa, copiado a un flujo que no
   * es el de una mesa.
   */
  const esDomicilio = tipo === 'delivery' && !esAgregar;
  const items = useCarritoStore((s) => s.items);
  const setCantidad = useCarritoStore((s) => s.setCantidad);
  const quitar = useCarritoStore((s) => s.quitar);
  const setNotas = useCarritoStore((s) => s.setNotas);
  const subtotal = useCarritoStore((s) => s.subtotal());
  const negocio = useNegocioStore((s) => s.negocio);

  /*
    Sprint 138 — Qué renglón se está anotando. La lista de botones se carga UNA vez para todo el
    carrito: una consulta por renglón sería una consulta por cada toque de "+".
  */
  const [anotando, setAnotando] = useState<{ uid: string; nombre: string } | null>(null);
  const gruposNotas = useNotasRapidas();

  const monedaSimbolo = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  // Sprint 7.5 + 23.1 — simular promos vigentes contra el carrito (debounced).
  // Sprint 23.1: además resolvemos nombres de productos regalo para preview legible.
  const [simRes, setSimRes] = useState<MotorResultado | null>(null);
  const [productosRefs, setProductosRefs] = useState<ProductoVista[]>([]);
  useEffect(() => {
    // Sprint 28 — en modo agregar las promos no se re-evalúan; no simulamos
    // para no mostrar un descuento que no se aplicará.
    if (esAgregar || items.length === 0) {
      setSimRes(null);
      return;
    }
    const carrito: MotorCarritoItem[] = items.map((i) => ({
      productoId: i.productoId,
      paqueteId: i.paqueteId,
      cantidad: i.cantidad,
      precioUnitario: i.precioUnitario,
    }));
    const t = setTimeout(async () => {
      const [resSim, resProd] = await Promise.all([
        window.api.promociones.simular(carrito),
        window.api.productos.listar(),
      ]);
      if (resSim.ok) setSimRes(resSim.data);
      else setSimRes(null);
      if (resProd.ok) setProductosRefs(resProd.data);
    }, 250);
    return () => clearTimeout(t);
  }, [items, esAgregar]);

  const descuento = simRes?.descuentoTotal ?? 0;
  const totalEstimado = Math.max(0, subtotal - descuento);
  const nombreProd = (id: number) => productosRefs.find((p) => p.id === id)?.nombre ?? `Producto #${id}`;

  return (
    <aside className="flex w-80 shrink-0 flex-col border-l border-slate-200 bg-white lg:w-96" data-tour="pos-carrito">
      <header className="border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2 font-semibold">
          <ShoppingCart className="h-5 w-5" />
          <span>{esAgregar ? 'Productos a agregar' : 'Pedido en curso'}</span>
        </div>
        <div className="mt-1 text-xs text-slate-500">
          {esAgregar
            ? /*
                 Sprint 140 — Agregar dejó de ser cosa sólo de mesas: decía "se suman a la cuenta
                 de la mesa" en una venta de mostrador y en un domicilio. Un canal que se nombra
                 mal se aprende mal, y quien lea el reporte tampoco sabrá qué está viendo.
               */
              destinoAgregar ?? 'Se suman a la cuenta'
            : paraRecoger
            ? 'Venta de mostrador'
            : tipo === 'comer_aqui'
            ? 'Comer aquí'
            : 'A domicilio'}
          {!esAgregar && clienteNombre && ` · ${clienteNombre}`}
        </div>
      </header>

      <div className="flex-1 overflow-auto px-4 py-3">
        {items.length === 0 ? (
          <div className="flex h-full items-center justify-center text-center text-sm text-slate-400">
            <div>
              <ShoppingCart className="mx-auto mb-2 h-8 w-8 opacity-50" />
              <p>Carrito vacío</p>
              <p className="text-xs">Selecciona productos del menú</p>
            </div>
          </div>
        ) : (
          <ul className="divide-y divide-slate-200">
            {items.map((i, indice) => {
              const lineaSubtotal = i.precioUnitario * i.cantidad;
              const esUltimo = i.cantidad <= 1;
              const topeStock =
                i.stockMax !== null && i.stockMax !== undefined && i.cantidad >= i.stockMax;
              // Sprint 78 — Lo que el servidor dijo de este renglón, si dijo algo.
              const problema = problemas.find((p) => p.indice === indice);
              return (
                <li
                  key={i.uid}
                  className={`flex flex-wrap items-center gap-2 py-1.5 ${
                    problema ? 'rounded-lg bg-rose-50 px-2 ring-1 ring-rose-200' : ''
                  }`}
                >
                  {/*
                    El nombre y su precio unitario en una sola columna elástica. `min-w-0` es lo
                    que permite que `truncate` funcione dentro de un flex — sin eso el nombre
                    largo empuja los botones fuera del carrito.
                  */}
                  {problema && (
                    <p className="order-last w-full text-xs font-medium text-rose-700">
                      ⚠ {problema.motivo}
                    </p>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium leading-tight" title={i.nombre}>
                      {i.nombre}
                    </div>
                    <div className="flex items-baseline gap-1.5 text-[11px] leading-tight text-slate-500">
                      <span className="font-mono">
                        {formatPrecio(i.precioUnitario, monedaSimbolo, locale)} c/u
                      </span>
                      {/*
                        El detalle del paquete se conserva en UNA línea truncada: es para
                        confirmar de reojo, no para leerse completo aquí. El texto entero sigue
                        disponible en el `title` y en la comanda.
                      */}
                      {i.paqueteProductos && i.paqueteProductos.length > 0 && (
                        <span
                          className="truncate"
                          title={i.paqueteProductos
                            .slice()
                            .sort((a, b) => a.posicion - b.posicion)
                            .map((x) => x.productoNombre)
                            .join(' · ')}
                        >
                          ·{' '}
                          {i.paqueteProductos
                            .slice()
                            .sort((a, b) => a.posicion - b.posicion)
                            .map((x) => x.productoNombre)
                            .join(' · ')}
                        </span>
                      )}
                    </div>
                  </div>

                  {/*
                    Sprint 67 — Los controles en la MISMA fila, y la basura desaparece.

                    Al llegar a 1, el `[−]` se convierte en el botón de quitar: es el patrón de
                    Rappi y de casi cualquier POS, y es descubrible porque es el mismo botón que
                    el operador ya venía usando para bajar la cantidad. Eso libera una fila
                    completa de 44 px por producto sin quitarle nada al operador.
                  */}
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      onClick={() => (esUltimo ? quitar(i.uid) : setCantidad(i.uid, i.cantidad - 1))}
                      className={`flex min-h-[44px] min-w-[44px] items-center justify-center rounded border ${
                        esUltimo
                          ? 'border-red-200 text-red-500 hover:bg-red-50'
                          : 'border-slate-300 bg-white hover:bg-slate-100'
                      }`}
                      aria-label={esUltimo ? `Quitar ${i.nombre} del carrito` : 'Disminuir cantidad'}
                      title={esUltimo ? 'Quitar del pedido' : 'Uno menos'}
                    >
                      {esUltimo ? <Trash2 className="h-5 w-5" /> : <Minus className="h-5 w-5" />}
                    </button>
                    <span className="w-7 text-center font-mono text-base font-semibold">
                      {i.cantidad}
                    </span>
                    <button
                      onClick={() => setCantidad(i.uid, i.cantidad + 1)}
                      disabled={topeStock}
                      title={topeStock ? 'Sin más stock disponible' : 'Uno más'}
                      className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded border border-slate-300 bg-white hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
                      aria-label="Aumentar cantidad"
                    >
                      <Plus className="h-5 w-5" />
                    </button>
                  </div>

                  <div className="w-16 shrink-0 text-right font-mono text-sm font-bold">
                    {formatPrecio(lineaSubtotal, monedaSimbolo, locale)}
                  </div>

                  {/*
                    Sprint 138 — Cómo lo quiere el cliente, renglón por renglón.

                    Ocupa su propia fila y no un icono apretado junto a los controles: el negocio
                    reportó que *"en paquetes sí me da la opción, en venta en mostrador no"*, y un
                    botón que no se ve es exactamente lo mismo que no tenerlo. Cuando hay nota, el
                    botón **es** la nota: se lee sin abrir nada, que es como se confirma un pedido
                    en voz alta ("entonces, término medio").
                  */}
                  <button
                    type="button"
                    onClick={() => setAnotando({ uid: i.uid, nombre: i.nombre })}
                    className={`order-last flex min-h-[40px] w-full items-center gap-2 rounded-lg border px-2 text-left text-sm ${
                      i.notasEspeciales
                        ? 'border-orange-300 bg-orange-50 font-semibold text-orange-900'
                        : 'border-dashed border-slate-300 bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                    aria-label={
                      i.notasEspeciales
                        ? `Cambiar la nota de ${i.nombre}: ${i.notasEspeciales}`
                        : `Agregar nota para ${i.nombre}`
                    }
                  >
                    <StickyNote className="h-4 w-4 shrink-0" />
                    <span className="flex-1 truncate">
                      {i.notasEspeciales ?? '¿Cómo lo quiere? (término, corte…)'}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {/*
        Sprint 67 — El pie tiene techo.

        El preview de promociones vive aquí dentro y no tenía límite, así que **aplicar una
        promoción le robaba espacio a la lista de productos**: de 3 visibles se caía a 2. Ahora el
        bloque de promos scrollea dentro de sus 132 px y la lista deja de encogerse por algo que
        pasa en el pie.
      */}
      <footer className="shrink-0 border-t border-slate-200 bg-slate-50 px-4 py-2 space-y-1.5">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600">Subtotal</span>
          <span className="font-mono">
            {formatPrecio(subtotal, monedaSimbolo, locale)}
          </span>
        </div>
        {/* Sprint 23.1 — Preview de promos diferenciado por tipo:
            • Aditivas (descuento=0 + itemsRegalo) → "🎁 Cortesía: 1 × X" sin restar.
            • Sustractivas (descuento>0) → "−$X" como antes. */}
        {simRes && simRes.aplicaciones.length > 0 && (() => {
          const aditivas = simRes.aplicaciones.filter((a) => a.descuentoAplicado === 0 && a.itemsRegalo && a.itemsRegalo.length > 0);
          const sustractivas = simRes.aplicaciones.filter((a) => a.descuentoAplicado > 0);
          return (
            <div className="max-h-[132px] space-y-2 overflow-y-auto">
              {aditivas.length > 0 && (
                <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-2">
                  <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-emerald-800">
                    <Gift className="h-3.5 w-3.5" />
                    Cortesías incluidas
                  </div>
                  <ul className="space-y-0.5 text-[11px] text-emerald-900">
                    {aditivas.map((a) =>
                      (a.itemsRegalo ?? []).map((reg, idx) => (
                        <li key={`${a.promocionId}-${idx}`} className="flex items-baseline gap-1">
                          <span className="text-emerald-600 shrink-0">🎁</span>
                          <span className="flex-1">
                            <strong>{reg.cantidad}× {nombreProd(reg.productoId)}</strong>
                            <span className="text-[10px] text-emerald-700 block italic">
                              por: {a.nombre}
                            </span>
                          </span>
                          <span className="font-mono text-[10px] text-emerald-700">GRATIS</span>
                        </li>
                      )),
                    )}
                  </ul>
                </div>
              )}
              {sustractivas.length > 0 && (
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-2">
                  <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-emerald-800">
                    <Tag className="h-3.5 w-3.5" />
                    Descuentos aplicados
                  </div>
                  <ul className="space-y-0.5 text-[11px] text-emerald-900">
                    {sustractivas.map((a) => (
                      <li key={a.promocionId} className="flex justify-between">
                        <span className="truncate">{a.nombre}</span>
                        <span className="font-mono">
                          −{formatPrecio(a.descuentoAplicado, monedaSimbolo, locale)}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-1 flex justify-between border-t border-emerald-200 pt-1 text-xs font-semibold text-emerald-800">
                    <span>Descuento total</span>
                    <span className="font-mono">
                      −{formatPrecio(descuento, monedaSimbolo, locale)}
                    </span>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        <div className="flex items-center justify-between text-lg font-bold">
          <span>{descuento > 0 ? 'Total estimado' : 'Total'}</span>
          <span className="font-mono">
            {formatPrecio(totalEstimado, monedaSimbolo, locale)}
          </span>
        </div>
        <p className="text-[10px] text-slate-500 text-center">
          {esAgregar
            ? `${destinoAgregar ?? 'Se suman a la cuenta'} y van a cocina. Precio normal.`
            : esDomicilio
            ? /*
                Sprint 76 — Decía "Falta la dirección y el comprobante" con la dirección ya
                capturada: le pedía al operador ir a buscar algo que ya había hecho. Ahora
                nombra lo que de verdad falta a esta altura del flujo.

                Sprint 140 — Y deja de exigir el comprobante cuando el negocio ya eligió cobrar
                al entregar: con esa opción la comanda **sí** sale sin él, así que el aviso
                afirmaba lo contrario de lo que iba a pasar. Un aviso que se desmiente solo es
                el que enseña a no leer los avisos.
              */
              pagaAlEntregar
              ? 'Se cocina ahora y se cobra al entregar. Falta el costo del envío.'
              : 'Falta el costo del envío y el comprobante. La comanda sale hasta entonces.'
            : simRes && simRes.aplicaciones.length > 0
            ? 'Promos arriba son preview; se confirman al enviar a cocina.'
            : 'Impuestos y promos se confirman en cobro.'}
        </p>
        <div className="space-y-2">
          <button
            onClick={onConfirmar}
            data-uso="venta:confirmar"
            disabled={items.length === 0 || enviando}
            className="btn-primary btn-lg w-full disabled:opacity-50 disabled:cursor-not-allowed"
            data-tour="pos-carrito-confirmar"
          >
            {textoBotonConfirmar({ esAgregar, enviando, esDomicilio })}
          </button>
          <button
            onClick={onCancelar}
            className="btn-ghost w-full text-sm"
            disabled={enviando}
            data-tour="pos-carrito-cancelar"
          >
            {esAgregar ? 'Cancelar' : 'Cancelar pedido'}
          </button>
        </div>
      </footer>

      <NotaProductoModal
        abierto={anotando !== null}
        nombreProducto={anotando?.nombre ?? ''}
        notaActual={items.find((i) => i.uid === anotando?.uid)?.notasEspeciales ?? null}
        grupos={gruposNotas}
        onGuardar={(nota) => {
          if (anotando) setNotas(anotando.uid, nota);
        }}
        onCerrar={() => setAnotando(null)}
      />
    </aside>
  );
}

/**
 * Sprint 65 — Qué dice el botón de confirmar. En una función aparte porque son tres flujos
 * distintos y el ternario anidado ya no se leía.
 *
 * Sprint 76 — Decía **"Continuar: ¿a dónde lo llevamos?"** cuando la dirección se había
 * capturado tres pantallas antes. Lo reportó el negocio con la pregunta exacta: *"¿pero si ya
 * puse la dirección?"*.
 *
 * Era un resto del Sprint 66, que adelantó la dirección al paso 2 y dejó este rótulo apuntando
 * al paso viejo. La regla que se aplica: **un botón se rotula con lo que hay del otro lado**, y
 * aquí lo que hay es la pantalla titulada "¿Cuánto cuesta llevarlo?". El eco es literal a
 * propósito — si el botón promete una cosa y la pantalla entrega otra, el operador aprende a no
 * leer los rótulos, y el día que uno diga algo importante tampoco lo va a leer.
 */
export function textoBotonConfirmar(p: {
  esAgregar: boolean;
  enviando: boolean;
  esDomicilio: boolean;
}): string {
  if (p.esAgregar) return p.enviando ? 'Agregando...' : 'Agregar a la cuenta';
  if (p.esDomicilio) return p.enviando ? 'Guardando...' : 'Continuar: ¿cuánto cuesta llevarlo?';
  return p.enviando ? 'Enviando...' : 'Enviar a cocina';
}
